/**
 * These fragments and function exist so we can properly update the Apollo cache during the
 * submitMove action in RegistryValidatorActions.
 */
import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';

import {FolderSummary} from 'constants/FolderConstants';
import {declarativeWriteRESTObjsToApollo, writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {restToGraphQL} from 'graphql-client/declarative-translations/external/translate';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {withNonNullSource} from 'nav/graphql/folder/Folder';
import {RegistryItemDisplayLabelGraphQL} from 'registry/__generated/RegistryItemDisplayLabelGraphQL';
import {RegistryItemFolderGraphQL} from 'registry/__generated/RegistryItemFolderGraphQL';
import {RegistryItemNameGraphQL} from 'registry/__generated/RegistryItemNameGraphQL';
import {RegistryItemTagsGraphQL} from 'registry/__generated/RegistryItemTagsGraphQL';
import {bioentityIdToTypename} from 'registry/graphql/GraphQLBioentityTypes';

import {SubmitMoveResponse} from './RegistryValidatorDAO';

function writeBioentityAliasInfosToGQL(response: SubmitMoveResponse): void {
  const aliasesForBioentities = Object.entries(response.bioentityAliasInfosById).map(([id, bais]) => ({
    id,
    bioentityAliasInfos: bais,
  }));

  declarativeWriteRESTObjsToApollo('AliasesForBioentity', aliasesForBioentities);
}

const REGISTRY_ITEM_NAME_FRAGMENT: DocumentNode = gql`
  fragment RegistryItemNameGraphQL on RegistryItemInterface {
    id
    name
  }
`;

const RegistryItemNameFragment: FragmentWrapper<RegistryItemNameGraphQL> = {
  fragment: REGISTRY_ITEM_NAME_FRAGMENT,
  fragmentName: 'RegistryItemNameGraphQL',
};

function writeNamesToGQL(response: SubmitMoveResponse): void {
  const namesForBioentities = Object.entries(response.changedNamesById).map(([id, name]) => ({
    __typename: bioentityIdToTypename(id),
    id,
    name,
  }));

  writeFragmentsToCache(RegistryItemNameFragment, namesForBioentities);
}

const REGISTRY_ITEM_DISPLAY_LABEL_FRAGMENT: DocumentNode = gql`
  fragment RegistryItemDisplayLabelGraphQL on RegistryItemInterface {
    id
    displayLabel
  }
`;

const RegistryItemDisplayLabelFragment: FragmentWrapper<RegistryItemDisplayLabelGraphQL> = {
  fragment: REGISTRY_ITEM_DISPLAY_LABEL_FRAGMENT,
  fragmentName: 'RegistryItemDisplayLabelGraphQL',
};

function writeDisplayLabelsToGQL(response: SubmitMoveResponse): void {
  const displayLabels = Object.entries(response.changedDisplayLabelsById).map(([id, displayLabel]) => ({
    __typename: bioentityIdToTypename(id),
    id,
    displayLabel,
  }));

  writeFragmentsToCache(RegistryItemDisplayLabelFragment, displayLabels);
}

const REGISTRY_ITEM_TAGS_FRAGMENT: DocumentNode = gql`
  fragment RegistryItemTagsGraphQL on RegistryItemInterface {
    id
    tagsJson
  }
`;

const RegistryItemTagsFragment: FragmentWrapper<RegistryItemTagsGraphQL> = {
  fragment: REGISTRY_ITEM_TAGS_FRAGMENT,
  fragmentName: 'RegistryItemTagsGraphQL',
};

function writeTagsToGQL(response: SubmitMoveResponse): void {
  const tags = Object.entries(response.tagsById).map(([id, tagsJson]) => ({
    __typename: bioentityIdToTypename(id),
    id,
    tagsJson,
  }));

  writeFragmentsToCache(RegistryItemTagsFragment, tags);
}

const REGISTRY_ITEM_FOLDER_FRAGMENT: DocumentNode = gql`
  fragment RegistryItemFolderGraphQL on RegistryItemInterface {
    id
    folder {
      ...FolderSummaryGraphQL
    }
  }
  ${getFragmentWrapper('FolderSummary').fragment}
`;

const RegistryItemFolderFragment: FragmentWrapper<RegistryItemFolderGraphQL> = {
  fragment: REGISTRY_ITEM_FOLDER_FRAGMENT,
  fragmentName: 'RegistryItemFolderGraphQL',
};

function writeFolderMovesToGQL(bioentityIds: Array<string>, folder: FolderSummary): void {
  const folderGQL = restToGraphQL('FolderSummary', withNonNullSource(folder));
  const folderFragments = bioentityIds.map((id) => ({
    __typename: bioentityIdToTypename(id),
    id,
    folder: folderGQL,
  }));

  writeFragmentsToCache(RegistryItemFolderFragment, folderFragments);
}

export default function writeSubmitMoveUpdatesToGraphQL(
  response: SubmitMoveResponse,
  bioentityIds: Array<string>,
  folder: FolderSummary
): void {
  writeFolderMovesToGQL(bioentityIds, folder);
  writeBioentityAliasInfosToGQL(response);
  writeNamesToGQL(response);
  writeDisplayLabelsToGQL(response);
  writeTagsToGQL(response);
}
